//#region imports
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import html2canvas from 'html2canvas';
import { useDispatch, useSelector } from 'react-redux';
import { getAirportLocation, selectAllAirportLocations } from '_redux/slices/timeline/timeline-areas';
import { getFlightsByDateTime, getFlightAuditReport } from '_services/flight-audit-report-service';
import { getFlightById } from '_services/flight-service';
import { getFlightTasks } from '_services/timeline-service';
import { getAirlineNameFromFlightNumber } from '_utils/airline-names';
import {
    buildLegPings,
    buildPassengerAudits,
    describeJourney,
    formatAuditDate,
    formatAuditTime,
    formatPingRange,
    getActualArrivalMs,
    getPingKey,
    getPingRequests,
    getScheduledArrivalMs,
    getTaskQueryDates,
    selectArrivalTasks,
    summarisePings,
    toMillis,
} from '_utils/arrival-audit';
import ArrivalAuditMap from './components/arrival-audit-map';
import { downloadArrivalAuditExcel } from './arrival-audit-excel';
//#endregion

//#region styles
const NAVY = '#1B4F8A';
const BLUE = '#2E86C1';
const ROW_LT = '#D6E4F3';
const ROW_VLT = '#EAF4FB';
const WARN = '#C0392B';

const thStyle = {
    background: NAVY,
    color: '#fff',
    fontWeight: 600,
    fontSize: 12,
    padding: '6px 8px',
    border: `1px solid ${ROW_LT}`,
    whiteSpace: 'nowrap',
};
const tdStyle = { fontSize: 12, padding: '6px 8px', border: `1px solid ${ROW_LT}`, color: '#333', verticalAlign: 'top' };
const labelTdStyle = { ...tdStyle, color: BLUE, fontWeight: 600, width: 130, background: ROW_VLT };
const sectionTitleStyle = {
    fontSize: 16,
    fontWeight: 700,
    color: BLUE,
    borderBottom: `3px solid ${NAVY}`,
    paddingBottom: 4,
    margin: '20px 0 12px',
};
//#endregion

// Parallel ping lookups; each is a small Firestore range read
const PING_CONCURRENCY = 3;

/** Runs `fn` over `items` with at most `limit` in flight. */
const mapWithConcurrency = async (items, limit, fn) => {
    const results = new Array(items.length);
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const i = next++;
            results[i] = await fn(items[i]);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
};

const flightLabel = (f) => {
    const t = toMillis(f?.operationInfo?.time);
    return `${f.flightNumber} · ${f.departureAirport} → ${f.arrivalAirport}${t ? ` · ${formatAuditTime(t)}` : ''}`;
};

const BfsArrivalAuditReport = ({ iata, runReportRef, onRunLoadingChange, onCanRunChange }) => {
    const dispatch = useDispatch();
    const airportLocations = useSelector(selectAllAirportLocations);

    //#region state
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [flights, setFlights] = useState([]);
    const [flightsStatus, setFlightsStatus] = useState('idle');
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [report, setReport] = useState(null);
    const [exporting, setExporting] = useState(false);
    const mapRefs = useRef({});
    //#endregion

    //#region effects
    useEffect(() => {
        if (iata) dispatch(getAirportLocation(iata));
    }, [dispatch, iata]);

    // Arrivals for the selected local day. BFS is UTC+0/+1, so start an hour early and keep flightDate === date.
    useEffect(() => {
        if (!iata || !date) return;
        let cancelled = false;
        setFlightsStatus('loading');
        setSelectedFlight(null);
        const start = moment.utc(date).subtract(1, 'hour').toISOString();
        const end = moment.utc(date).endOf('day').toISOString();
        getFlightsByDateTime({ iata, startDateTime: start, endDateTime: end, type: 'inbound' })
            .then((res) => {
                if (cancelled) return;
                const list = (res?.flights || [])
                    .filter((f) => f.arrivalAirport === iata && (!f.flightDate || f.flightDate === date))
                    .sort((a, b) => (toMillis(a.operationInfo?.time) ?? 0) - (toMillis(b.operationInfo?.time) ?? 0));
                setFlights(list);
                setFlightsStatus('succeeded');
            })
            .catch(() => {
                if (cancelled) return;
                setFlights([]);
                setFlightsStatus('failed');
            });
        return () => { cancelled = true; };
    }, [iata, date]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(status === 'loading');
    }, [status, onRunLoadingChange]);

    useEffect(() => {
        if (onCanRunChange) onCanRunChange(!!selectedFlight && status !== 'loading');
    }, [selectedFlight, status, onCanRunChange]);
    //#endregion

    //#region run
    const runReport = useCallback(async () => {
        if (!selectedFlight || !iata) return;
        setStatus('loading');
        setError(null);
        setReport(null);
        mapRefs.current = {};
        try {
            // The dropdown list is a lightweight projection; the full doc carries operatingTimes (STA/ATA)
            const flight = await getFlightById(selectedFlight._id)
                .then((doc) => (doc?._id ? doc : selectedFlight))
                .catch(() => selectedFlight);
            const staMs = getScheduledArrivalMs(flight);
            const flightNumber = flight.flightNumber || selectedFlight.flightNumber;
            const flightDate = flight.flightDate || flight.date || selectedFlight.flightDate || date;

            // Task flightDate is the UTC date, so a 00:xx BST arrival sits on the previous day
            const taskLists = await Promise.all(
                getTaskQueryDates(flightDate, staMs).map((d) => getFlightTasks({ iata, flightNumber, date: d }))
            );
            const tasks = selectArrivalTasks(taskLists.flat(), { iata, flightNumber, staMs });
            const audits = buildPassengerAudits(tasks, { staMs });

            const requests = getPingRequests(audits);
            const responses = await mapWithConcurrency(requests, PING_CONCURRENCY, (req) =>
                getFlightAuditReport({
                    iata,
                    employeeId: req.employeeId,
                    startDate: new Date(req.start).toISOString(),
                    endDate: new Date(req.end).toISOString(),
                })
                    .then((data) => ({ key: req.key, data }))
                    .catch((e) => ({ key: req.key, error: e.message || 'Failed to load pings' }))
            );
            const rawPings = Object.fromEntries(responses.map((r) => [r.key, r]));

            setReport({
                flightDetails: {
                    date: formatAuditDate(staMs) || moment(flightDate).format('DD/MM/YYYY'),
                    airline: getAirlineNameFromFlightNumber(flightNumber, flight.carrierIATA),
                    flightNumber,
                    sta: formatAuditTime(staMs),
                    ata: formatAuditTime(getActualArrivalMs(flight)),
                    origin: flight.departureAirport,
                    gate: flight.gate && flight.gate !== 'N/A' ? flight.gate : '',
                },
                audits,
                rawPings,
            });
            setStatus('succeeded');
        } catch (e) {
            setError(e.message || 'Failed to generate the audit report');
            setStatus('failed');
        }
    }, [selectedFlight, iata, date]);

    useEffect(() => {
        if (!runReportRef) return;
        runReportRef.current = runReport;
        return () => { runReportRef.current = null; };
    }, [runReportRef, runReport]);
    //#endregion

    //#region derived
    // Resolved here (not at fetch time) so pings re-resolve once airport locations arrive
    const pingsByKey = useMemo(() => {
        if (!report) return {};
        const out = {};
        for (const audit of report.audits) {
            for (const leg of audit.legs) {
                const key = getPingKey(leg);
                if (out[key] || !leg.window) continue;
                const raw = report.rawPings[key];
                const pings = raw?.data ? buildLegPings(raw.data, leg.window, airportLocations || []) : [];
                out[key] = { pings, summary: summarisePings(pings), error: raw?.error || null };
            }
        }
        return out;
    }, [report, airportLocations]);
    //#endregion

    //#region export
    const handleExport = async () => {
        if (!report) return;
        setExporting(true);
        try {
            const mapImages = {};
            for (const audit of report.audits) {
                const el = mapRefs.current[audit.key];
                if (!el) continue;
                try {
                    const canvas = await html2canvas(el, { useCORS: true, scale: 1, logging: false });
                    mapImages[audit.key] = canvas.toDataURL('image/jpeg', 0.85);
                } catch (e) {
                    // A tainted canvas (tile CORS) only loses that map image; the row still exports
                    console.warn('Map capture failed for', audit.name, e);
                }
            }
            await downloadArrivalAuditExcel({
                flightDetails: report.flightDetails,
                audits: report.audits,
                pingsByKey,
                mapImages,
                fileName: `BFS_Arrival_Audit_${report.flightDetails.flightNumber}_${date}.xlsx`,
            });
        } finally {
            setExporting(false);
        }
    };
    //#endregion

    //#region render
    const flightOptions = flights.map((f) => ({ value: f._id, label: flightLabel(f), flight: f }));
    const selectedOption = selectedFlight ? flightOptions.find((o) => o.value === selectedFlight._id) || null : null;

    return (
        <div style={{ marginBottom: 200 }}>
            <Row className="g-3" style={{ alignItems: 'flex-end' }}>
                <Col xs={12} md={3}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>Arrival date</Form.Label>
                    <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Col>
                <Col xs={12} md={5}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>Arrival flight</Form.Label>
                    <Select
                        options={flightOptions}
                        value={selectedOption}
                        onChange={(opt) => setSelectedFlight(opt ? opt.flight : null)}
                        isClearable
                        isLoading={flightsStatus === 'loading'}
                        placeholder={flightsStatus === 'failed' ? 'Failed to load flights' : 'Type a flight number…'}
                        noOptionsMessage={() => (flightsStatus === 'loading' ? 'Loading…' : 'No arrivals on this date')}
                    />
                </Col>
            </Row>

            {status === 'loading' && <p style={{ marginTop: 24, color: '#666' }}>Generating audit…</p>}
            {status === 'failed' && <p style={{ marginTop: 24, color: WARN }}>Error: {error}</p>}
            {status === 'idle' && (
                <p style={{ marginTop: 24, color: '#666' }}>Select a date and an arrival flight, then run the report.</p>
            )}

            {status === 'succeeded' && report && (
                <div style={{ marginTop: 24, fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ ...sectionTitleStyle, margin: 0, borderBottom: 'none' }}>
                            BFS Arrivals Audit – {report.flightDetails.flightNumber}
                        </div>
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={exporting || !report.audits.length}
                            style={{
                                padding: '8px 14px',
                                background: exporting ? '#93c5fd' : NAVY,
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                fontSize: 14,
                                cursor: exporting ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <i className="fas fa-file-excel" style={{ marginRight: 6 }} />
                            {exporting ? 'Preparing Excel…' : 'Download Excel'}
                        </button>
                    </div>

                    {/* Flight details */}
                    <div style={sectionTitleStyle}>1. Flight Details</div>
                    <table style={{ borderCollapse: 'collapse', minWidth: 420 }}>
                        <tbody>
                            {[
                                ['Date', report.flightDetails.date],
                                ['Airline', report.flightDetails.airline],
                                ['Flight No', report.flightDetails.flightNumber],
                                ['STA', report.flightDetails.sta],
                                ['ATA', report.flightDetails.ata],
                                ['Origin', report.flightDetails.origin],
                                ['Arrival Gate', report.flightDetails.gate],
                            ].map(([label, value], i) => (
                                <tr key={label}>
                                    <td style={labelTdStyle}>{label}</td>
                                    <td style={{ ...tdStyle, background: i % 2 ? ROW_VLT : '#fff' }}>{value || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Overview */}
                    <div style={sectionTitleStyle}>2. Passenger Overview ({report.audits.length})</div>
                    {report.audits.length === 0 ? (
                        <p style={{ color: '#666' }}>No passenger tasks found for this arrival.</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                    <tr>
                                        {['#', 'Passenger Name', 'Pax Type', 'SSR', 'Agent(s)', 'Confirm', 'At Location', 'Start', 'Finish', 'Comments'].map((h) => (
                                            <th key={h} style={thStyle}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.audits.map((a, i) => (
                                        <tr key={a.key} style={{ background: i % 2 ? ROW_VLT : '#fff' }}>
                                            <td style={tdStyle}>{i + 1}</td>
                                            <td style={tdStyle}><a href={`#audit-${i + 1}`}>{a.name}</a></td>
                                            <td style={tdStyle}>{a.adviceStatus || 'N/A'}</td>
                                            <td style={tdStyle}>{a.assistance.join(', ') || 'N/A'}</td>
                                            <td style={tdStyle}>{a.agents.join(', ')}</td>
                                            <td style={tdStyle}>{formatAuditTime(a.confirm) || 'N/A'}</td>
                                            <td style={tdStyle}>{formatAuditTime(a.atLocation) || 'N/A'}</td>
                                            <td style={tdStyle}>{formatAuditTime(a.start) || 'N/A'}</td>
                                            <td style={tdStyle}>{formatAuditTime(a.finish) || 'Not finished'}</td>
                                            <td style={{ ...tdStyle, background: '#fff', minWidth: 160 }} />
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                                Comments are left blank – add them in the Excel download.
                            </p>
                        </div>
                    )}

                    {/* Per passenger */}
                    {report.audits.length > 0 && <div style={sectionTitleStyle}>3. Passenger Audit Trail</div>}
                    {report.audits.map((a, i) => {
                        const legPings = a.legs.map((leg) => ({ leg, pings: pingsByKey[getPingKey(leg)]?.pings || [] }));
                        return (
                            <div key={a.key} id={`audit-${i + 1}`} style={{ border: `1px solid ${ROW_LT}`, marginBottom: 20 }}>
                                <div style={{ background: NAVY, color: '#fff', padding: '8px 12px', fontSize: 14 }}>
                                    <strong>{i + 1}. {a.name}</strong>
                                    <span style={{ color: ROW_LT }}>
                                        {'  |  '}{a.adviceStatus || 'Pax type N/A'}
                                        {a.assistance.length ? `  |  ${a.assistance.join(', ')}` : ''}
                                    </span>
                                </div>
                                <Row className="g-3" style={{ padding: 12 }}>
                                    <Col xs={12} lg={6}>
                                        <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12 }}>
                                            <tbody>
                                                <tr><td style={labelTdStyle}>Agent(s)</td><td style={tdStyle}>{a.agents.join(', ')}</td></tr>
                                                <tr><td style={labelTdStyle}>Journey</td><td style={tdStyle}>{describeJourney(a.legs)}</td></tr>
                                                <tr><td style={labelTdStyle}>Confirm time</td><td style={tdStyle}>{formatAuditTime(a.confirm) || 'N/A'}</td></tr>
                                                <tr><td style={labelTdStyle}>At location</td><td style={tdStyle}>{formatAuditTime(a.atLocation) || 'N/A'}</td></tr>
                                                <tr><td style={labelTdStyle}>Start time</td><td style={tdStyle}>{formatAuditTime(a.start) || 'N/A'}</td></tr>
                                                <tr><td style={labelTdStyle}>Finish time</td><td style={tdStyle}>{formatAuditTime(a.finish) || 'Not finished'}</td></tr>
                                            </tbody>
                                        </table>

                                        {a.legs.map((leg) => {
                                            const p = pingsByKey[getPingKey(leg)];
                                            return (
                                                <div key={leg.taskId} style={{ marginBottom: 12 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 4 }}>
                                                        {leg.type} – {leg.employeeName}
                                                        <span style={{ fontWeight: 400, color: '#666' }}>
                                                            {'  '}Allocated {formatAuditTime(leg.allocated) || 'N/A'} · Confirm {formatAuditTime(leg.confirm) || 'N/A'} · At loc {formatAuditTime(leg.atLocation) || 'N/A'} · Start {formatAuditTime(leg.start) || 'N/A'} · Finish {formatAuditTime(leg.finish) || 'open'}
                                                        </span>
                                                    </div>
                                                    {!leg.employeeId || !leg.window ? (
                                                        <div style={{ fontSize: 12, color: '#666' }}>No allocation window – pings not queried.</div>
                                                    ) : p?.error ? (
                                                        <div style={{ fontSize: 12, color: WARN }}>Pings failed to load: {p.error}</div>
                                                    ) : !p?.summary?.length ? (
                                                        <div style={{ fontSize: 12, color: '#666' }}>No GPS/beacon pings between allocation and finish.</div>
                                                    ) : (
                                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={thStyle}>Time</th>
                                                                    <th style={thStyle}>Location</th>
                                                                    <th style={thStyle}>Source</th>
                                                                    <th style={thStyle}>Pings</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {p.summary.map((row, k) => (
                                                                    <tr key={k} style={{ background: k % 2 ? ROW_VLT : '#fff' }}>
                                                                        <td style={tdStyle}>{formatPingRange(row)}</td>
                                                                        <td style={tdStyle}>{row.location}</td>
                                                                        <td style={tdStyle}>{row.sources}</td>
                                                                        <td style={tdStyle}>{row.count}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                    {leg.window?.open && (
                                                        <div style={{ fontSize: 11, color: WARN, marginTop: 2 }}>
                                                            Important – leg not finished; pings shown up to {formatAuditTime(leg.window.end)}.
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </Col>
                                    <Col xs={12} lg={6}>
                                        <ArrivalAuditMap
                                            ref={(el) => { mapRefs.current[a.key] = el; }}
                                            legPings={legPings}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
    //#endregion
};

export default BfsArrivalAuditReport;
